import { Alert, App, Button, Popconfirm, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";

import { useCandidates, useSetEnabled } from "../../api/candidates";
import { formatTime } from "../../api/format";
import { useNames } from "../../api/names";
import type { CandidateSummary } from "../../api/types";
import { usePermission } from "../../auth/SessionProvider";
import { CandidateDrawer } from "./CandidateDrawer";
import { CandidateFilterBar, type CandidateFilters } from "./CandidateFilterBar";
import { GroupBatchModal } from "./GroupBatchModal";
import { SiteBatchModal } from "./SiteBatchModal";

const PAGE_SIZE = 50;

export function CandidatesPage() {
  const { groupName, regionName, siteName, roomName } = useNames();
  const { message } = App.useApp();
  const canDisable = usePermission("candidate:disable");
  const canEnable = usePermission("candidate:enable");
  const [filters, setFilters] = useState<CandidateFilters>({});
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [selected, setSelected] = useState<string[]>([]);
  const [openUsername, setOpenUsername] = useState<string>();
  const [batchOpen, setBatchOpen] = useState(false);
  const [groupBatchOpen, setGroupBatchOpen] = useState(false);

  const list = useCandidates({ ...filters, order, page, pageSize: PAGE_SIZE });
  const setEnabledMutation = useSetEnabled();

  const apply = async (usernames: string[], value: boolean) => {
    try {
      const result = await setEnabledMutation.mutateAsync({ usernames, enabled: value });
      message.success(`${value ? "开放" : "关闭"}了 ${result.updated} 个账号${result.failed.length ? `，失败 ${result.failed.length} 个` : ""}`);
      setSelected([]);
    } catch (caught) {
      message.error(caught instanceof Error ? caught.message : "操作失败");
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <CandidateFilterBar
        value={filters}
        onChange={(next) => { setFilters(next); setPage(1); setSelected([]); }}
        refreshing={list.isFetching}
        onRefresh={() => void list.refetch()}
      />
      <Space wrap>
        {canDisable && (
          <Popconfirm title={`关闭所选的 ${selected.length} 个账号？`} disabled={selected.length === 0} onConfirm={() => void apply(selected, false)}>
            <Button danger disabled={selected.length === 0}>关闭所选</Button>
          </Popconfirm>
        )}
        {canEnable && (
          <Popconfirm title={`开放所选的 ${selected.length} 个账号？`} disabled={selected.length === 0} onConfirm={() => void apply(selected, true)}>
            <Button disabled={selected.length === 0}>开放所选</Button>
          </Popconfirm>
        )}
        {(canDisable || canEnable) && <Button onClick={() => setBatchOpen(true)}>按考点/考场批量操作</Button>}
        {(canDisable || canEnable) && <Button danger onClick={() => setGroupBatchOpen(true)}>按组别批量操作</Button>}
        <Typography.Text type="secondary">共 {list.data?.total ?? 0} 人{selected.length > 0 && `，已选 ${selected.length} 人`}</Typography.Text>
      </Space>
      {list.error && <Alert type="error" showIcon message={list.error.message} />}
      <Table<CandidateSummary>
        rowKey="username"
        size="middle"
        loading={list.isLoading}
        dataSource={list.data?.items ?? []}
        rowSelection={(canDisable || canEnable) ? { selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]) } : undefined}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: list.data?.total ?? 0,
          showSizeChanger: false,
          showTotal: (total) => `共 ${total} 人`,
          onChange: (next) => { setPage(next); setSelected([]); }
        }}
        onChange={(_, __, sorter) => {
          // Sorting is server side because the list is paginated.
          const next = (Array.isArray(sorter) ? sorter[0] : sorter)?.order === "descend" ? "desc" : "asc";
          if (next !== order) { setOrder(next); setPage(1); setSelected([]); }
        }}
        columns={[
          { title: "准考证号", dataIndex: "username", sorter: true, sortOrder: order === "asc" ? "ascend" : "descend", sortDirections: ["ascend", "descend", "ascend"],
            render: (value: string) => <a onClick={() => setOpenUsername(value)}>{value}</a> },
          { title: "姓名", dataIndex: "name" },
          { title: "省份", dataIndex: "province", render: (value: string | null) => value ?? "—" },
          { title: "组别", dataIndex: "group", render: groupName },
          { title: "考点区域", dataIndex: "regionId", render: regionName },
          { title: "考点", dataIndex: "siteId", render: siteName },
          { title: "考场", dataIndex: "roomId", render: roomName },
          { title: "座位", dataIndex: "seat", render: (value: string | null) => value ?? "—" },
          { title: "状态", render: (_, row) => row.enabled ? <Tag color="green">启用</Tag> : <Tag color="red">已关闭</Tag> },
          { title: "最近登录", render: (_, row) => (
            <><div>{formatTime(row.lastLoginAt)}</div><Typography.Text type="secondary">{row.lastLoginIp ?? ""}</Typography.Text></>
          ) },
          { title: "最近提交", render: (_, row) => (
            <><div>{formatTime(row.lastSubmitAt)}</div><Typography.Text type="secondary">{row.lastSubmitIp ?? ""}</Typography.Text></>
          ) },
          { title: "操作", render: (_, row) => row.enabled
            ? canDisable && <Popconfirm title={`关闭 ${row.username} 的账号？`} onConfirm={() => void apply([row.username], false)}><a>关闭</a></Popconfirm>
            : canEnable && <Popconfirm title={`开放 ${row.username} 的账号？`} onConfirm={() => void apply([row.username], true)}><a>开放</a></Popconfirm> }
        ]}
      />
      <CandidateDrawer username={openUsername} onClose={() => setOpenUsername(undefined)} />
      <SiteBatchModal open={batchOpen} initialSiteId={filters.siteId} initialRoomId={filters.roomId} onClose={() => setBatchOpen(false)} />
      <GroupBatchModal open={groupBatchOpen} onClose={() => setGroupBatchOpen(false)} />
    </Space>
  );
}
